import React, { useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';

import Heading from '@tds/core-heading';
import Text from '@tds/core-text';
import Box from '@tds/core-box';
import Radio from '@tds/core-radio';
import Button from '@tds/core-button';
import Notification from '@tds/core-notification';
import Axios from 'axios';

import { getEnvironment } from '../../../utils/request';
import { useLocalizedStringsContext } from '../../../context/LocalizedStringsContext';
import { Error } from '../../../design/elements';

const Form = ({ setFormSubmitted }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    reportAnIssue: {
      formTitle,
      form: {
        labelDefectWebform,
        labelDefectWebformOptionBusinessConnect,
        labelDefectWebformOptionWirelineSecurity,
        labelDefectWebformOptionWireless,
        labelDefectWebformOptionMyod,
        labelDefectWebformOptionPhoneNumberCheck,
        labelDefectWebformOptionContactCentre,
        labelDefectWebformOptionReferralTool,
        labelDefectCategory,
        labelDefectCategoryOptionUserInfo,
        labelDefectCategoryOptionOffersPricing,
        labelDefectCategoryOptionBehaviouralIssue,
        labelPriority,
        labelPriorityOptionLow,
        labelPriorityOptionMedium,
        labelPriorityOptionHigh,
        labelPriorityOptionUrgent,
        labelIssueSummary,
        placeHolderIssueSummary,
        labelErrorMessage,
        placeHolderErrorMessage,
        labelStepsToReplicate,
        placeHolderStepsToReplicate,
        labelAttachements,
        attachementsNote,
        buttonSubmit
      }
    }
  } = localizedStrings;
  const [formFieldData, setFormFieldData] = useState({
    defectWebform: {
      isRequired: true,
      error: false,
      value: ''
    },
    defectCategory: {
      isRequired: true,
      error: false,
      value: ''
    },
    priority: {
      isRequired: true,
      error: false,
      value: ''
    },
    issueSummary: {
      isRequired: true,
      error: false,
      value: ''
    },
    errorMessage: {
      isRequired: true,
      error: false,
      value: ''
    },
    stepsToReplicate: {
      isRequired: true,
      error: false,
      value: ''
    },
    attachement: {
      isRequired: false,
      error: false,
      value: ''
    }
  });
  const [error, setError] = useState();

  const submitForm = () => {
    let hasError = false;
    const fieldData = { ...formFieldData };
    const formData = new FormData();
    Object.keys(fieldData).forEach(field => {
      let fieldValue =
        field === 'attachement'
          ? fieldData[field].value[0]
          : fieldData[field].value;
      fieldValue = fieldValue || '';
      if (fieldValue === '' && fieldData[field].isRequired) {
        fieldData[field].error = true;
        hasError = true;
      } else formData.append(field, fieldValue);
    });

    const userData = JSON.parse(localStorage.getItem('userProfile'));
    const { name, email } = userData || {};
    formData.append('name', name);
    formData.append('email', email);
    const reportIssueSummary = formData.get('issueSummary');
    const reportErrorMessage = formData.get('errorMessage');
    const replicateProblem = formData.get('stepsToReplicate');
    const reportIssueAttachments = formData.get('attachement');
    formData.append('reportIssueSummary', reportIssueSummary);
    formData.append('reportErrorMessage', reportErrorMessage);
    formData.append('replicateProblem', replicateProblem);
    formData.append('reportIssueAttachments', reportIssueAttachments);

    formData.delete('issueSummary');
    formData.delete('errorMessage');
    formData.delete('stepsToReplicate');

    if (hasError) {
      setFormFieldData(fieldData);
      return;
    }

    const { DEFECTS_ACCESS_TOKEN, sof2ApiHost } = getEnvironment();
    Axios({
      method: 'post',
      url: `${sof2ApiHost}/defects/create`,
      headers: {
        access_token: DEFECTS_ACCESS_TOKEN,
        'Access-Control-Allow-Origin': '*'
      },
      data: formData
    })
      .then(() => setFormSubmitted(true))
      .catch(err => {
        setError(_.get(err, 'response.data.message', err.message));
      });
  };

  const setFieldValue = e => {
    const fieldName = e.target.name;
    const fieldValue =
      fieldName === 'attachement' ? e.target.files : e.target.value;

    setFormFieldData({
      ...formFieldData,
      [fieldName]: {
        ...formFieldData[fieldName],
        value: fieldValue,
        error: false
      }
    });
  };

  return (
    <>
      <Heading level="h2">{formTitle}</Heading>
      <Box tag="fieldset" between={2}>
        <legend>
          <Text bold size="medium">
            {labelDefectWebform}
          </Text>
        </legend>
        {[
          labelDefectWebformOptionBusinessConnect,
          labelDefectWebformOptionWirelineSecurity,
          labelDefectWebformOptionWireless,
          labelDefectWebformOptionMyod,
          labelDefectWebformOptionPhoneNumberCheck,
          labelDefectWebformOptionContactCentre,
          labelDefectWebformOptionReferralTool
        ].map(option => (
          <Radio
            label={option}
            name="defectWebform"
            value={option}
            checked={formFieldData.defectWebform.value === option}
            onChange={setFieldValue}
          />
        ))}
        {formFieldData.defectCategory.error && (
          <Error text="Field is required" />
        )}
      </Box>
      <Box tag="fieldset" between={2}>
        <legend>
          <Text bold size="medium">
            {labelDefectCategory}
          </Text>
        </legend>
        {[
          labelDefectCategoryOptionUserInfo,
          labelDefectCategoryOptionOffersPricing,
          labelDefectCategoryOptionBehaviouralIssue
        ].map(option => (
          <Radio
            label={option}
            name="defectCategory"
            value={option}
            checked={formFieldData.defectCategory.value === option}
            onChange={setFieldValue}
          />
        ))}
        {formFieldData.defectCategory.error && (
          <Error text="Field is required" />
        )}
      </Box>
      <Box tag="fieldset" between={2}>
        <legend>
          <Text bold size="medium">
            {labelPriority}
          </Text>
        </legend>
        {[
          labelPriorityOptionLow,
          labelPriorityOptionMedium,
          labelPriorityOptionHigh,
          labelPriorityOptionUrgent
        ].map(option => (
          <Radio
            label={option}
            name="priority"
            value={option}
            checked={formFieldData.priority.value === option}
            onChange={setFieldValue}
          />
        ))}
        {formFieldData.priority.error && <Error text="Field is required" />}
      </Box>
      <Box between={2}>
        <Text bold size="medium">
          {labelIssueSummary}
        </Text>
        <input
          label={labelIssueSummary}
          id="issueSummary"
          name="issueSummary"
          type="text"
          maxLength="100"
          placeholder={placeHolderIssueSummary}
          className="frg-input-field color-light"
          value={formFieldData.issueSummary.value}
          onChange={setFieldValue}
        />
        {formFieldData.issueSummary.error && <Error text="Field is required" />}
      </Box>
      <Box between={2}>
        <Text bold size="medium">
          {labelErrorMessage}
        </Text>
        <textarea
          name="errorMessage"
          rows="4"
          cols="50"
          maxLength="100"
          className="frg-input-field color-light"
          placeholder={placeHolderErrorMessage}
          onChange={setFieldValue}
        />
        {formFieldData.errorMessage.error && <Error text="Field is required" />}
      </Box>
      <Box between={2}>
        <Text bold size="medium">
          {labelStepsToReplicate}
        </Text>
        <textarea
          name="stepsToReplicate"
          rows="4"
          cols="50"
          maxLength="500"
          className="frg-input-field color-light"
          placeholder={placeHolderStepsToReplicate}
          onChange={setFieldValue}
        />
        {formFieldData.stepsToReplicate.error && (
          <Error text="Field is required" />
        )}
      </Box>
      <Box between={2}>
        <Text bold size="medium">
          {labelAttachements}
        </Text>
        <p style={{ fontStyle: 'italic', color: 'red' }}>{attachementsNote}</p>
        <input
          type="file"
          name="attachement"
          accept="image/*"
          onChange={setFieldValue}
        />
      </Box>
      <div style={{ width: '30%' }}>
        <Button onClick={submitForm}>{buttonSubmit}</Button>
      </div>
      {error && (
        <Notification variant="error" copy="en">
          <Text bold>{error}</Text>
        </Notification>
      )}
    </>
  );
};

Form.propTypes = {
  setFormSubmitted: PropTypes.func.isRequired
};

export default Form;
